import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, Page, SearchInput, SimplePagination, Table, Tabs, } from '@dixa/lib-shared-habla';
import { getHighestUserRole, USER_ROLE_ATTRIBUTES } from 'lib-shared-user-role';
import { memo, useCallback, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { analytics } from 'app/utils/segment';
import { IfAgentHasRole } from 'components/shared/agentRole';
import { TableFailedRow } from 'components/shared/tableFailedRow';
import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import { userModalActions } from 'components/usersModal';
import { useAgents } from 'hooks/useAgents';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { AgentsWorkloadForm } from '../containers/agentsWorkloadForm';
import { useUpdateAgent } from '../hooks/useUpdateAgent';
import styles from './agents.module.scss';
import { AgentsTableRow } from './agentsTableRow';
const NUMBER_OF_COLUMNS = 5;
var AgentsTabs;
(function (AgentsTabs) {
    AgentsTabs["AgentsListTab"] = "Agents";
    AgentsTabs["WorkloadFormTab"] = "Workloads";
})(AgentsTabs || (AgentsTabs = {}));
const tabsDefinition = Object.values(AgentsTabs).map((tabName) => ({
    id: tabName,
    label: tabName,
}));
export const Agents = memo(function Agents() {
    const { items: agents, total, asyncStatus, params, nextPage, previousPage, hasNextPage, hasPreviousPage, changeParams, updateItem: updateAgent, } = useAgents({ limit: 50 });
    useUpdateAgent(updateAgent);
    const { currentAgent } = useAppSelector((state) => ({ currentAgent: state.settings.profile.profile }), shallowEqual);
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState(AgentsTabs.AgentsListTab);
    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        analytics.settingsAgentsTabClicked({ tab: tab.toLowerCase() });
    }, []);
    const handleAddAgentsClick = () => {
        analytics.settingsAgentsAddAgentButtonClicked();
        dispatch(userModalActions.show({
            user: { roles: ['agent'] },
        }));
    };
    const renderBody = () => {
        if (asyncStatus === 'loading') {
            return _jsx(TableLoadingRow, { colSpan: NUMBER_OF_COLUMNS });
        }
        if (asyncStatus === 'failed') {
            return (_jsx(TableFailedRow, { colSpan: NUMBER_OF_COLUMNS, handleRetry: () => changeParams({}) }));
        }
        if (agents.length === 0) {
            return (_jsx(Table.Tr, { children: _jsx(Table.Td, Object.assign({ colSpan: NUMBER_OF_COLUMNS }, { children: "No agents found." })) }));
        }
        const currentAgentHighestRole = getHighestUserRole(currentAgent.roles);
        const { privilegeLevel: currentAgentPrivilegeLevel } = USER_ROLE_ATTRIBUTES[currentAgentHighestRole];
        return agents.map((agent) => {
            const { id, roles } = agent;
            const { privilegeLevel: agentPrivilegeLevel } = USER_ROLE_ATTRIBUTES[getHighestUserRole(roles)];
            return (_jsx(AgentsTableRow, { canEdit: currentAgentHighestRole === 'admin' ||
                    currentAgentPrivilegeLevel < agentPrivilegeLevel, agent: agent, showEditAgentModal: () => dispatch(userModalActions.show({ user: agent })) }, id));
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx(Page.Tabs, { children: _jsx(Tabs, { selectedId: activeTab, tabs: tabsDefinition, onChange: handleTabChange }) }), _jsxs(Page.Body, { children: [activeTab === AgentsTabs.AgentsListTab && (_jsx(Page.Section, Object.assign({ title: "All agents", titleMeta: total && (_jsx(Badge, Object.assign({ size: "small", variant: "secondary" }, { children: total }))), controls: _jsxs(_Fragment, { children: [_jsx(SearchInput, { onSubmit: (value) => {
                                        changeParams({ search: value }, { debounceTime: 300 });
                                    }, onToggle: (isOpen) => !isOpen && params.search && changeParams({ search: '' }) }), _jsx(IfAgentHasRole, Object.assign({ role: 'systemadmin' }, { children: _jsx(Button, Object.assign({ className: styles.agentsCta, onClick: handleAddAgentsClick, id: "add-agent-btn" }, { children: "Add agent" })) }))] }) }, { children: _jsxs(Table, Object.assign({ framed: true, stickyHeader: true, stickyFooter: true, headerWithBackground: true }, { children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, { children: "Name" }), _jsx(Table.Th, { children: "Display Name" }), _jsx(Table.Th, Object.assign({ className: styles.headCellEmail }, { children: "Email" })), _jsx(Table.Th, Object.assign({ className: styles.headCellRole }, { children: "Role" })), _jsx(Table.Th, { className: styles.headCellActions })] }) }), _jsx(Table.Tbody, { children: renderBody() }), (hasNextPage || hasPreviousPage) && (_jsx(Table.Tfoot, Object.assign({ className: styles.agentsTableFoot }, { children: _jsx(Table.Tr, { children: _jsx(Table.Td, Object.assign({ colSpan: NUMBER_OF_COLUMNS }, { children: _jsx(SimplePagination, { previousButtonClick: previousPage, previousButtonDisabled: !hasPreviousPage, nextButtonClick: nextPage, nextButtonDisabled: !hasNextPage, loading: asyncStatus === 'loading' }) })) }) })))] })) }))), activeTab === AgentsTabs.WorkloadFormTab && _jsx(AgentsWorkloadForm, {})] })] }));
});
